import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import PhoneInput, {
  getCountryCallingCode,
  isPossiblePhoneNumber,
} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';

/**
 * Normalize a stored phone value into E.164 before handing it to PhoneInput.
 * Values may come from external imports and can be corrupted (e.g.
 * "+undefinedundefined", or "47 912 34 567" without the plus). Anything that
 * can't be normalized to a valid number is treated as empty so the user
 * enters it fresh.
 */
const normalizePhoneValue = (value, defaultCountry) => {
  if (!value || typeof value !== 'string') {
    return undefined;
  }
  const hasPlus = value.startsWith('+');
  const digits = value.replace(/\D/g, '');
  const callingCode = String(getCountryCallingCode(defaultCountry));
  let candidate;
  if (hasPlus) {
    candidate = `+${digits}`;
  } else if (digits.startsWith('00')) {
    candidate = `+${digits.slice(2)}`;
  } else if (digits.startsWith(callingCode)) {
    candidate = `+${digits}`;
  } else if (isPossiblePhoneNumber(digits, defaultCountry)) {
    candidate = `+${callingCode}${digits}`;
  }
  return candidate && isPossiblePhoneNumber(candidate) ? candidate : undefined;
};

const CountryPhone = (props) => {
  const { input, meta, placeholder, onSearch, searchLabel, searchDisabled } =
    props;
  const config = useConfig();
  let defaultCountry;
  switch (config.currencyCaps) {
    case 'NOK':
      defaultCountry = 'NO';
      break;
    case 'SEK':
      defaultCountry = 'SE';
      break;
    case 'GBP':
      defaultCountry = 'GB';
      break;
    default:
      defaultCountry = 'NO';
  }
  const [country, setCountry] = useState(defaultCountry);
  const s = partial('shared');

  // input.value can be a string (from initialize) or an object { phone, code }
  // (from a previous onChange). PhoneInput expects a plain string.
  // Only sanitize initialized string values (possibly imported/corrupted):
  // values produced by user typing are passed through untouched, because
  // while typing they are incomplete or too long and isPossiblePhoneNumber
  // would clear the field mid-typing.
  const isTypedValue = input.value && typeof input.value === 'object';
  const rawValue = isTypedValue ? input.value.phone : input.value;
  const phoneValue = isTypedValue
    ? rawValue
    : normalizePhoneValue(rawValue, defaultCountry);

  return (
    <div className="form__form-group-input-wrap">
      <div className={onSearch ? 'phone-search-row' : undefined}>
        <PhoneInput
          style={{ width: '100%' }}
          defaultCountry={defaultCountry}
          value={phoneValue}
          placeholder={placeholder}
          onCountryChange={setCountry}
          onChange={(value) =>
            input.onChange({
              phone: value,
              code: getCountryCallingCode(country || defaultCountry),
            })
          }
        />
        {onSearch && (
          <Button
            type="button"
            color="primary"
            size="sm"
            className="phone-search-row__button"
            disabled={searchDisabled}
            onClick={() => onSearch(phoneValue)}
          >
            <MagnifyIcon size={16} /> {searchLabel}
          </Button>
        )}
      </div>
      {meta.touched && meta.error && (
        <span className="form__form-group-error">{meta.error}</span>
      )}
    </div>
  );
};

CountryPhone.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onChange: PropTypes.func,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }).isRequired,
  placeholder: PropTypes.string,
  onSearch: PropTypes.func,
  searchLabel: PropTypes.string,
  searchDisabled: PropTypes.bool,
};

CountryPhone.defaultProps = {
  placeholder: '',
  onSearch: undefined,
  searchLabel: '',
  searchDisabled: false,
};

export default CountryPhone;

/**
 * Extract a clean E.164 phone string from a form value produced by CountryPhone.
 * CountryPhone stores either a plain string (from initialize) or an object
 * { phone, code } (from the onChange handler). This returns the international
 * phone number with all whitespace removed, or undefined when no value was entered.
 */
export const extractPhoneValue = (value) => {
  const raw = value && typeof value === 'object' ? value.phone : value;
  return raw?.replace(/\s/g, '') || undefined;
};
